import {
  ClaimSensitivity,
  ExperienceTemplate,
  ExperienceTemplateId,
  ExperienceSurface,
  ExperienceVertical,
  FlowConfig,
  FlowResult,
  GemSalesConfig,
  GeneratedExperience,
  GeneratedExperienceSection,
} from "./types";

export interface ExperienceTemplateQuery {
  intent?: string;
  surface?: ExperienceSurface;
  vertical?: ExperienceVertical;
  claimSensitivity?: ClaimSensitivity;
  requiresMerchantApproval?: boolean;
}

export interface ExperienceTemplateReadiness {
  templateId: ExperienceTemplateId;
  ready: boolean;
  missingInputs: string[];
  warnings: string[];
}

export const EXPERIENCE_TEMPLATES: Record<ExperienceTemplateId, ExperienceTemplate> = {
  wellness_product_fit_result_v1: {
    id: "wellness_product_fit_result_v1",
    label: "Wellness product fit result",
    family: "decision",
    vertical: "wellness",
    intents: ["product_fit", "format_choice", "trust_safety", "energy_focus", "routine_requested"],
    surfaces: ["drawer", "inline_block", "dynamic_page"],
    requiredInputs: ["answers", "catalog"],
    optionalInputs: ["proof_assets", "approved_copy", "acquisition_context"],
    claimSensitivity: "high",
    requiresMerchantApproval: true,
  },
  wellness_proof_explainer_v1: {
    id: "wellness_proof_explainer_v1",
    label: "Wellness proof explainer",
    family: "trust_proof",
    vertical: "wellness",
    intents: ["lab_proof", "trust_safety", "ingredient_proof"],
    surfaces: ["drawer", "inline_block", "dynamic_page"],
    requiredInputs: ["proof_topic", "approved_proof_snippets"],
    optionalInputs: ["proof_assets", "source_links"],
    claimSensitivity: "high",
    requiresMerchantApproval: true,
  },
  wellness_cart_addon_v1: {
    id: "wellness_cart_addon_v1",
    label: "Wellness cart add-on",
    family: "bundle_aov",
    vertical: "wellness",
    intents: ["free_shipping_opportunity", "price_value", "commercial_intent"],
    surfaces: ["drawer", "inline_block"],
    requiredInputs: ["cart_value", "catalog", "product_pairing_rules"],
    optionalInputs: ["free_shipping_threshold", "goal", "inventory"],
    claimSensitivity: "medium",
    requiresMerchantApproval: true,
  },
  wellness_format_comparator_v1: {
    id: "wellness_format_comparator_v1",
    label: "Wellness format comparator",
    family: "decision",
    vertical: "supplements",
    intents: ["format_choice", "compare_products", "taste_concern", "convenience_concern"],
    surfaces: ["drawer", "inline_block", "dynamic_page"],
    requiredInputs: ["catalog", "format_options"],
    optionalInputs: ["taste_preference", "routine_context", "proof_assets"],
    claimSensitivity: "high",
    requiresMerchantApproval: true,
  },
  wellness_objection_resolver_v1: {
    id: "wellness_objection_resolver_v1",
    label: "Wellness objection resolver",
    family: "safety_compliance",
    vertical: "wellness",
    intents: ["unresolved_objection", "price_value", "taste_concern", "medication_condition", "shipping_returns"],
    surfaces: ["drawer", "inline_block", "dynamic_page"],
    requiredInputs: ["objection", "approved_copy"],
    optionalInputs: ["policy_copy", "proof_assets", "product_context"],
    claimSensitivity: "high",
    requiresMerchantApproval: true,
  },
  wellness_routine_timeline_v1: {
    id: "wellness_routine_timeline_v1",
    label: "Wellness routine timeline",
    family: "routine",
    vertical: "wellness",
    intents: ["routine_requested", "timeline_expectation", "post_purchase_guidance"],
    surfaces: ["drawer", "inline_block", "dynamic_page", "email"],
    requiredInputs: ["goal", "merchant_approved_usage_copy"],
    optionalInputs: ["product_refs", "timeline_horizon", "subscription_preference"],
    claimSensitivity: "high",
    requiresMerchantApproval: true,
  },
  wellness_stack_builder_v1: {
    id: "wellness_stack_builder_v1",
    label: "Wellness stack builder",
    family: "bundle_aov",
    vertical: "supplements",
    intents: ["bundle_builder", "routine_requested", "aov", "subscription_fit"],
    surfaces: ["drawer", "inline_block", "dynamic_page"],
    requiredInputs: ["goal", "catalog", "product_pairing_rules"],
    optionalInputs: ["dietary_restrictions", "subscription_preference", "cart_value"],
    claimSensitivity: "high",
    requiresMerchantApproval: true,
  },
};

const FALLBACK_TEMPLATE_BY_FLOW_ID: Record<string, ExperienceTemplateId> = {
  "wn-homepage-goal-router-v1": "wellness_product_fit_result_v1",
  "wn-shilasource-fit-v1": "wellness_product_fit_result_v1",
  "wn-advertorial-story-router-v1": "wellness_product_fit_result_v1",
  "wn-proof-explainer-v1": "wellness_proof_explainer_v1",
  "wn-cart-addon-v1": "wellness_cart_addon_v1",
};

export function selectExperienceTemplateId(flow: FlowConfig, result: FlowResult): ExperienceTemplateId | null {
  const templateId = result.templateId ?? flow.templateId ?? FALLBACK_TEMPLATE_BY_FLOW_ID[flow.id] ?? null;
  if (!templateId || !EXPERIENCE_TEMPLATES[templateId]) return null;

  return templateId;
}

export function listExperienceTemplates(query: ExperienceTemplateQuery = {}): ExperienceTemplate[] {
  return Object.values(EXPERIENCE_TEMPLATES).filter((template) => matchesTemplateQuery(template, query));
}

export function getExperienceTemplate(templateId: ExperienceTemplateId): ExperienceTemplate {
  return EXPERIENCE_TEMPLATES[templateId];
}

export function assessExperienceTemplateReadiness(templateId: ExperienceTemplateId, config: GemSalesConfig): ExperienceTemplateReadiness {
  const template = getExperienceTemplate(templateId);
  const missingInputs = template.requiredInputs.filter((input) => !hasTemplateInput(input, config));
  const warnings: string[] = [];

  if (template.claimSensitivity === "high" && !config.approvedCopy.safetyCaveat) {
    warnings.push("High-sensitivity templates should include an approved safety caveat.");
  }
  if (template.requiresMerchantApproval && !config.approvedCopy.blockedClaims.length) {
    warnings.push("Merchant-approved blocked claims should be configured before enabling this template.");
  }

  return {
    templateId,
    ready: missingInputs.length === 0,
    missingInputs,
    warnings,
  };
}

export function listExperienceTemplateReadiness(config: GemSalesConfig): ExperienceTemplateReadiness[] {
  return Object.keys(EXPERIENCE_TEMPLATES).map((templateId) =>
    assessExperienceTemplateReadiness(templateId as ExperienceTemplateId, config),
  );
}

function matchesTemplateQuery(template: ExperienceTemplate, query: ExperienceTemplateQuery): boolean {
  if (query.intent && !template.intents.includes(query.intent)) return false;
  if (query.surface && !template.surfaces.includes(query.surface)) return false;
  if (query.vertical && template.vertical !== query.vertical && template.vertical !== "wellness") return false;
  if (query.claimSensitivity && template.claimSensitivity !== query.claimSensitivity) return false;
  if (query.requiresMerchantApproval !== undefined && template.requiresMerchantApproval !== query.requiresMerchantApproval) return false;

  return true;
}

function hasTemplateInput(input: string, config: GemSalesConfig): boolean {
  if (input === "answers") return true;
  if (input === "catalog") return Object.keys(config.products).length > 0;
  if (input === "proof_topic") return true;
  if (input === "approved_proof_snippets") return Object.keys(config.approvedCopy.approvedProofSnippets).length > 0;
  if (input === "cart_value") return config.commerce.freeShippingThreshold > 0;
  if (input === "product_pairing_rules") return Object.keys(config.products).length > 1;
  if (input === "format_options") return new Set(Object.values(config.products).map((product) => product.format).filter(Boolean)).size > 1;
  if (input === "objection") return true;
  if (input === "approved_copy") return Boolean(config.approvedCopy.fallbackProofResponse && config.approvedCopy.valueResponse);
  if (input === "goal") return true;
  if (input === "merchant_approved_usage_copy") return Boolean(config.approvedCopy.safetyCaveat && config.approvedCopy.guaranteeSummary);

  return false;
}

export function buildGeneratedExperience(
  flow: FlowConfig,
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperience | null {
  if (result.experience) return validateGeneratedExperience(result.experience, config) ? result.experience : null;

  const templateId = selectExperienceTemplateId(flow, result);
  if (!templateId) return null;

  const primaryCta = result.ctaUrl
    ? {
        label: result.ctaLabel,
        url: result.ctaUrl,
        productHandle: result.recommendedProductHandle ?? null,
      }
    : null;
  const secondaryCta =
    result.secondaryUrl && result.secondaryLabel
      ? {
          label: result.secondaryLabel,
          url: result.secondaryUrl,
          productHandle: null,
        }
      : null;

  const experience: GeneratedExperience = {
    id: `exp_${flow.id}_${templateId}`,
    templateId,
    title: result.title,
    surface: "drawer",
    sections: buildSections(templateId, answers, result, config),
    primaryCta,
    secondaryCta,
    recommendedProductHandle: result.recommendedProductHandle ?? null,
  };

  return validateGeneratedExperience(experience, config) ? experience : null;
}

export function validateGeneratedExperience(experience: GeneratedExperience, config: GemSalesConfig): boolean {
  if (!experience.id || !experience.templateId || !experience.title || !experience.sections.length) return false;
  if (!experience.sections.every((section) => validateSection(section, config))) return false;
  if (experience.primaryCta && !isAllowedUrl(experience.primaryCta.url)) return false;
  if (experience.secondaryCta && !isAllowedUrl(experience.secondaryCta.url)) return false;

  return true;
}

function buildSections(
  templateId: ExperienceTemplateId,
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  if (templateId === "wellness_proof_explainer_v1") return buildProofSections(answers, result, config);
  if (templateId === "wellness_cart_addon_v1") return buildCartAddOnSections(answers, result, config);
  if (templateId === "wellness_format_comparator_v1") return buildFormatComparatorSections(answers, result, config);
  if (templateId === "wellness_objection_resolver_v1") return buildObjectionResolverSections(answers, result, config);
  if (templateId === "wellness_routine_timeline_v1") return buildRoutineTimelineSections(answers, result, config);
  if (templateId === "wellness_stack_builder_v1") return buildStackBuilderSections(answers, result, config);

  return buildProductFitSections(answers, result, config);
}

function buildProductFitSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const sections: GeneratedExperienceSection[] = [
    {
      type: "summary",
      title: "Your buying context",
      body: summarizeAnswers(answers),
    },
  ];

  const product = result.recommendedProductHandle ? config.products[result.recommendedProductHandle] : null;
  if (product) {
    sections.push(
      buildProductSection({
        productHandle: product.handle,
        title: product.title,
        body: result.body,
        price: product.price,
        format: product.format,
        url: product.url,
        ctaLabel: result.ctaLabel,
      }),
    );
  } else {
    sections.push({
      type: "summary",
      title: result.title,
      body: result.body,
    });
  }

  if (answers.format === "easy" || answers.hesitation === "taste" || answers.priority === "taste") {
    sections.push({
      type: "comparison",
      title: "Format tradeoff",
      rows: [
        { label: "Resin", value: "Best when proof and potency matter", detail: "Requires taste/prep tolerance." },
        { label: "Gummies", value: "Best when convenience matters", detail: "Useful if taste is the main hesitation." },
      ],
    });
  }

  sections.push({
    type: "proof",
    title: "Proof boundary",
    body: config.approvedCopy.labProofSummary,
  });

  if (result.caution) {
    sections.push({
      type: "caution",
      body: result.caution,
    });
  }

  return sections;
}

function buildProofSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const topic = answers.proof_topic ?? answers.proof ?? "proof";

  return [
    {
      type: "proof",
      title: formatAnswer(topic),
      body: result.body,
    },
    {
      type: "comparison",
      title: "How to use this proof",
      rows: [
        { label: "Good for", value: "Identity, purity, sourcing, and safety signals" },
        { label: "Not for", value: "Treatment claims or guaranteed health outcomes" },
      ],
    },
    {
      type: "caution",
      body: result.caution ?? config.approvedCopy.safetyCaveat,
    },
  ];
}

function buildCartAddOnSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const sections: GeneratedExperienceSection[] = [
    {
      type: "metric",
      label: "Free shipping threshold",
      value: `${config.commerce.currency} ${config.commerce.freeShippingThreshold}`,
      detail: "Show this only when current cart rules approve an add-on.",
    },
  ];

  const product = result.recommendedProductHandle ? config.products[result.recommendedProductHandle] : null;
  if (product) {
    sections.push(
      buildProductSection({
        productHandle: product.handle,
        title: product.title,
        body: `Goal selected: ${formatAnswer(answers.goal ?? "add-on")}. ${result.body}`,
        price: product.price,
        format: product.format,
        url: product.url,
        ctaLabel: result.ctaLabel,
      }),
    );
  } else {
    sections.push({
      type: "summary",
      title: result.title,
      body: result.body,
    });
  }

  return sections;
}

function buildFormatComparatorSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const sections: GeneratedExperienceSection[] = [
    {
      type: "summary",
      title: "Format priority",
      body: summarizeAnswers(answers),
    },
    {
      type: "comparison",
      title: "Resin vs gummies",
      rows: [
        {
          label: "Resin",
          value: "Best when proof, traditional format, and potency positioning matter most.",
          detail: "Requires taste and mixing tolerance.",
        },
        {
          label: "Gummies",
          value: "Best when convenience, taste, and travel matter most.",
          detail: "Useful if daily adherence is the main risk.",
        },
        {
          label: "Proof boundary",
          value: "Use lab proof to assess identity, purity, and safety signals.",
          detail: "Do not treat format choice as a medical outcome promise.",
        },
      ],
    },
  ];

  const product = result.recommendedProductHandle ? config.products[result.recommendedProductHandle] : null;
  if (product) {
    sections.push(
      buildProductSection({
        productHandle: product.handle,
        title: product.title,
        body: result.body,
        price: product.price,
        format: product.format,
        url: product.url,
        ctaLabel: result.ctaLabel,
      }),
    );
  } else {
    sections.push({
      type: "summary",
      title: result.title,
      body: result.body,
    });
  }

  sections.push({
    type: "caution",
    body: result.caution ?? config.approvedCopy.safetyCaveat,
  });

  return sections;
}

function buildObjectionResolverSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const objection = answers.objection ?? answers.hesitation ?? answers.concern ?? "unsure";
  const sections: GeneratedExperienceSection[] = [
    {
      type: "summary",
      title: "Question to resolve",
      body: `Main concern: ${formatAnswer(objection)}.`,
    },
    {
      type: "proof",
      title: "Short answer",
      body: result.body,
    },
  ];

  if (objection === "price" || objection === "value" || objection === "guarantee") {
    sections.push({
      type: "proof",
      title: "Value and policy context",
      body: config.approvedCopy.guaranteeSummary,
    });
  } else if (objection === "taste") {
    sections.push({
      type: "comparison",
      title: "Taste tradeoff",
      rows: [
        { label: "Resin", value: "Stronger taste, best for shoppers comfortable mixing into a drink." },
        { label: "Gummies", value: "Easier habit if taste or convenience is the main risk." },
      ],
    });
  } else if (objection === "timeline") {
    sections.push({
      type: "comparison",
      title: "Expectation boundary",
      rows: [
        { label: "Useful", value: "Use merchant guidance to understand routine fit and consistency." },
        { label: "Not promised", value: "No guaranteed timeline or health outcome should be implied." },
      ],
    });
  } else {
    sections.push({
      type: "caution",
      body: result.caution ?? config.approvedCopy.safetyCaveat,
    });
  }

  const product = result.recommendedProductHandle ? config.products[result.recommendedProductHandle] : null;
  if (product) {
    sections.push(
      buildProductSection({
        productHandle: product.handle,
        title: product.title,
        body: "Review the product page only after this concern feels resolved.",
        price: product.price,
        format: product.format,
        url: product.url,
        ctaLabel: result.ctaLabel,
      }),
    );
  }

  return sections;
}

function buildRoutineTimelineSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const sections: GeneratedExperienceSection[] = [
    {
      type: "summary",
      title: "Routine goal",
      body: summarizeAnswers(answers),
    },
    {
      type: "timeline",
      title: "7/30/90-day expectation guide",
      items: [
        {
          label: "Days 1-7",
          body: "Focus on fit: taste, preparation, timing, and whether the routine is easy enough to repeat.",
        },
        {
          label: "Days 8-30",
          body: "Keep the routine consistent and use product proof, guarantee, and safety guidance to decide whether it belongs in your daily habit.",
        },
        {
          label: "Days 31-90",
          body: "Review value, reorder preference, and whether another format would make the habit easier. Do not treat the timeline as a guaranteed outcome.",
        },
      ],
    },
    {
      type: "proof",
      title: "Expectation boundary",
      body: result.body,
    },
    {
      type: "caution",
      body: result.caution ?? config.approvedCopy.safetyCaveat,
    },
  ];

  const product = result.recommendedProductHandle ? config.products[result.recommendedProductHandle] : null;
  if (product) {
    sections.push(
      buildProductSection({
        productHandle: product.handle,
        title: product.title,
        body: "Review the product page and approved usage guidance before starting the routine.",
        price: product.price,
        format: product.format,
        url: product.url,
        ctaLabel: result.ctaLabel,
      }),
    );
  }

  return sections;
}

function buildStackBuilderSections(
  answers: Record<string, string>,
  result: FlowResult,
  config: GemSalesConfig,
): GeneratedExperienceSection[] {
  const sections: GeneratedExperienceSection[] = [
    {
      type: "summary",
      title: "Stack goal",
      body: summarizeAnswers(answers),
    },
    {
      type: "comparison",
      title: "Why this stack is shown",
      rows: [
        {
          label: "Base product",
          value: "Start with the product that best matches the selected goal.",
        },
        {
          label: "Add-on",
          value: "Only show add-ons that match merchant-approved pairing rules.",
        },
        {
          label: "Boundary",
          value: "A stack is a shopping bundle, not a promise of stronger health results.",
        },
      ],
    },
  ];

  const product = result.recommendedProductHandle ? config.products[result.recommendedProductHandle] : null;
  if (product) {
    sections.push(
      buildProductSection({
        productHandle: product.handle,
        title: product.title,
        body: result.body,
        price: product.price,
        format: product.format,
        url: product.url,
        ctaLabel: result.ctaLabel,
      }),
    );
  }

  sections.push({
    type: "caution",
    body: result.caution ?? config.approvedCopy.safetyCaveat,
  });

  return sections;
}

function buildProductSection(input: {
  productHandle: string;
  title: string;
  body: string;
  price?: number | undefined;
  format?: string | undefined;
  url?: string | undefined;
  ctaLabel?: string | undefined;
}): GeneratedExperienceSection {
  return {
    type: "product",
    productHandle: input.productHandle,
    title: input.title,
    body: input.body,
    ...(input.price === undefined ? {} : { price: input.price }),
    ...(input.format === undefined ? {} : { format: input.format }),
    ...(input.url === undefined ? {} : { url: input.url }),
    ...(input.ctaLabel === undefined ? {} : { ctaLabel: input.ctaLabel }),
  };
}

function validateSection(section: GeneratedExperienceSection, config: GemSalesConfig): boolean {
  if (section.type === "product") {
    const product = config.products[section.productHandle];
    if (!product || product.handle !== section.productHandle) return false;
    if (section.url && !isAllowedUrl(section.url)) return false;
  }

  if (section.type === "comparison") return section.rows.length > 0;
  if (section.type === "timeline") return section.items.length > 0;
  if (section.type === "caution") return Boolean(section.body.trim());

  return true;
}

function summarizeAnswers(answers: Record<string, string>): string {
  const entries = Object.entries(answers);
  if (!entries.length) return "GemSales used the current page context to choose the next step.";

  return entries.map(([key, value]) => `${formatAnswer(key)}: ${formatAnswer(value)}`).join(". ");
}

function formatAnswer(value: string): string {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function isAllowedUrl(url: string): boolean {
  return url.startsWith("/") || url.startsWith("#") || url.startsWith("https://") || url.startsWith("http://");
}
